/**
 * ViewToolbar Component
 *
 * Main toolbar component for views with page info, view switcher, and filters.
 */

import type { ReactNode } from 'react';
import { ListTodo, LayoutGrid, GanttChart, PanelLeft, PanelRight, ChevronRight } from 'lucide-react';
import type { ViewMode } from '@/types';
import clsx from 'clsx';

export interface BreadcrumbItem {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
}

export interface ViewToolbarProps {
  // Main view switcher (only for list/board/gantt)
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  showViewSwitcher?: boolean;

  // Page info
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];

  // Left side content (after view switcher)
  leftContent?: ReactNode;

  // Right side content
  rightContent?: ReactNode;

  // Expandable filters
  filterContent?: ReactNode;

  // Custom class
  className?: string;

  // Mobile sidebar toggle
  onOpenSidebar?: () => void;
  // Mobile secondary sidebar toggle
  onOpenSecondarySidebar?: () => void;
}

export function ViewToolbar({
  viewMode,
  onViewModeChange,
  showViewSwitcher = false,
  icon,
  title,
  subtitle,
  breadcrumbs,
  leftContent,
  rightContent,
  filterContent,
  className,
  onOpenSidebar,
  onOpenSecondarySidebar,
}: ViewToolbarProps) {
  return (
    <div
      className={clsx(
        'bg-surface-secondary/70 shrink-0 sticky top-0 z-30 border-b border-border-primary/60 backdrop-blur supports-backdrop-filter:backdrop-blur-sm',
        className
      )}
    >
      {/* Main Toolbar */}
      <div className="flex flex-col gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
            {onOpenSidebar && (
              <button
                onClick={onOpenSidebar}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-lg border border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover transition-colors"
                aria-label="Apri sidebar"
              >
                <PanelLeft className="w-4 h-4 text-text-secondary" />
              </button>
            )}
            {onOpenSecondarySidebar && (
              <button
                onClick={onOpenSecondarySidebar}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-lg border border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover transition-colors"
                aria-label="Apri pannello aggiuntivo"
              >
                <PanelRight className="w-4 h-4 text-text-secondary" />
              </button>
            )}
            {/* Page Icon & Title with Breadcrumb */}
            {(icon || title) && (
              <div className="flex items-center gap-3 min-w-0 shrink-0">
                {icon && (
                  <div className="w-8 h-8 rounded-lg bg-surface-tertiary/50 flex items-center justify-center shrink-0">
                    {icon}
                  </div>
                )}
                {title && (
                  <div className="min-w-0 flex flex-col">
                    {/* Breadcrumb above title */}
                    {breadcrumbs && breadcrumbs.length > 0 && (
                      <nav
                        className="flex items-center gap-1 mb-0.5 text-[9px] text-text-quaternary"
                        aria-label="Breadcrumb"
                      >
                        {breadcrumbs.map((item, index) => (
                          <div key={index} className="flex items-center gap-1">
                            {index > 0 && (
                              <ChevronRight className="w-2 h-2 text-text-quaternary shrink-0" />
                            )}
                            {item.onClick ? (
                              <button
                                onClick={item.onClick}
                                className="hover:text-text-secondary transition-colors truncate"
                              >
                                {item.label}
                              </button>
                            ) : (
                              <span className="text-text-tertiary truncate">{item.label}</span>
                            )}
                          </div>
                        ))}
                      </nav>
                    )}
                    {breadcrumbs && breadcrumbs.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <h2 className="text-[12px] sm:text-[13px] font-semibold text-text-primary truncate">
                          {title}
                        </h2>
                        {subtitle && (
                          <span className="text-[10px] sm:text-[11px] text-text-quaternary tabular-nums font-medium shrink-0 min-w-[88px] whitespace-nowrap">
                            {subtitle}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <h2 className="text-[12px] sm:text-[13px] font-semibold text-text-primary truncate">
                          {title}
                        </h2>
                        {subtitle && (
                          <p className="text-[9px] sm:text-[10px] text-text-quaternary truncate tabular-nums min-w-[88px] whitespace-nowrap">
                            {subtitle}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* View Switcher */}
            {showViewSwitcher && onViewModeChange && (
              <div className="flex items-center gap-0.5 p-0.5 bg-surface-tertiary/50 rounded-lg shrink-0">
                {[
                  { mode: 'list' as ViewMode, icon: ListTodo, label: 'Lista' },
                  { mode: 'board' as ViewMode, icon: LayoutGrid, label: 'Board' },
                  { mode: 'gantt' as ViewMode, icon: GanttChart, label: 'Gantt' },
                ].map(({ mode, icon: Icon, label }) => (
                  <button
                    key={mode}
                    onClick={() => onViewModeChange(mode)}
                    title={label}
                    className={clsx(
                      'flex items-center gap-1.5 px-2 py-1 text-[10px] sm:px-2.5 sm:py-1.5 sm:text-[11px] rounded-md font-medium transition-all',
                      viewMode === mode
                        ? 'bg-surface-primary text-text-primary shadow-sm'
                        : 'text-text-tertiary hover:text-text-secondary hover:bg-surface-hover/50'
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Left Content (filters, grouping, etc) */}
            {leftContent && (
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap min-w-0 w-full sm:w-auto">
                {leftContent}
              </div>
            )}
          </div>

          {/* Right Content */}
          {rightContent && (
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-start sm:justify-end w-full sm:w-auto min-w-0">
              {rightContent}
            </div>
          )}
        </div>
      </div>

      {/* Expandable Filter Panel */}
      {filterContent && (
        <div className="px-4 py-3 bg-surface-tertiary/20 border-t border-border-primary/60">
          {filterContent}
        </div>
      )}
    </div>
  );
}
